import { Box, Step, Typography, styled } from "@mui/material";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import clsx from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PopUpSteps } from "../../../DesignEditor/constants/mock-data.ts";
import stepperConnectorIcon from "../../../assets/images/svgs/stepper-connector.svg";
import BriaTooltip from "../BriaTooltip/BriaTooltip.tsx";
import styles from "./HorizontalLinearStepper.module.scss";

interface IHorizontalLinearStepper {
	steps: string[];
	activeStep?: number;
	setActiveStep?: (value: number) => void;
	size?: string;
	readonly?: boolean;
	disabledStep?: number[] | undefined;
	disableAll?: boolean | undefined;
	icon?: ReactNode;
	disableClick?: boolean;
}

const StyledStepper = styled(Stepper)(({ theme }) => ({
	"& .MuiStepButton-root": {
		cursor: "inherit",
	},
	"& .Mui-active .MuiStepIcon-root": {
		color: theme.palette.primary.light,
		border: `2px solid ${theme.palette.primary.light}`,
		borderRadius: "50%",
		background: theme.palette.primary.light,
	},
	"& .Mui-active .MuiTypography-root": {
		fontWeight: theme.typography.fontWeightMedium,
	},
	"& .MuiStep-root": {
		paddingTop: 4,
		paddingBottom: 4,
	},
	"& .MuiStep-root:first-child .stepConnectorIcon": {
		display: "none",
	},
	"& .Mui-disabled .MuiStepIcon-root": {
		color: "white",
		border: `2px solid ${theme.palette.primary.light}`,
		borderRadius: "50%",
	},
	"& .Mui-disabled .MuiStepLabel-labelContainer": {
		color: "#5B5B5B",
	},
	"& .Mui-disabled .MuiStepIcon-root text": { fill: "black", fontWeight: "600" },
	"& .Mui-active .MuiStepIcon-root text": { fontWeight: "600" },

	"& .Mui-completed .MuiStepIcon-root": {
		color: theme.palette.primary.light,
		border: `2px solid ${theme.palette.primary.light}`,
		borderRadius: "50%",
	},
	"& .MuiStepLabel-iconContainer": {
		paddingRight: 0,
		marginRight: 8,
	},
}));

const HorizontalLinearStepper = ({
	steps,
	activeStep,
	setActiveStep = () => {},
	size,
	readonly,
	disabledStep = [],
	disableAll,
	icon,
	disableClick = false,
}: IHorizontalLinearStepper) => {
	const { t } = useTranslation("translation", { keyPrefix: "editor.popup.linearHorizontalStepper" });

	const returnToolTipMessage = (stepIndex: number): string => {
		const step = PopUpSteps[stepIndex];
		if (!step) return "";

		switch (step) {
			case "images":
				return t("smartImagesDisabledToolTip");
			case "WizardImages":
				return t("wizardImagesDisabledToolTip");
			case "brands":
				return t("brandsDisabledToolTip");
			default:
				return "";
		}
	};

	const handleStepClick = (index: number) => {
		if (disableClick) return;
		setActiveStep(index);
	};
	return (
		<StyledStepper
			className={clsx(styles.container, { [styles.readonly]: readonly, [styles.big]: size === "large" })}
			activeStep={activeStep}
		>
			{steps.map((label, index) => {
				const isDisabled = disableAll || (Array.isArray(disabledStep) && disabledStep.includes(index));
				return (
					<Step key={label} className={clsx({ [styles.disabled]: isDisabled })}>
						<BriaTooltip
							title={returnToolTipMessage(index)}
							disable={!isDisabled || !returnToolTipMessage(index)}
						>
							{icon ? (
								<Box className={styles.arrowStepConnector}>
									<img className="stepConnectorIcon" src={stepperConnectorIcon} />
									<StepButton
										disableRipple
										onClick={() => (isDisabled ? null : handleStepClick(index))}
									>
										<Typography className={styles.stepButton}>{label}</Typography>
									</StepButton>
								</Box>
							) : (
								<StepButton disableRipple onClick={() => (isDisabled ? null : handleStepClick(index))}>
									<Typography className={styles.stepButton}>{label}</Typography>
								</StepButton>
							)}
						</BriaTooltip>
					</Step>
				);
			})}
		</StyledStepper>
	);
};

export default HorizontalLinearStepper;
